import { useState, useEffect } from "react";
import { Form } from "antd";
import dayjs from "dayjs";
import Button from "@/common/elements/Button";
import CommonDatePicker from "@/common/elements/DatePicker";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import Table from "@/common/elements/Table";
import { StyledFlex, StyledGridCol, StyledGridRow, StyledSectionHeading, StyledSubTableContainer } from "@/common/styles";
import CustomModal from "@/components/CustomModal";
import { TableHeader } from "@/components/InmateDetails/styles";
import { Checkbox } from "antd";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { getOptionsFromRefList } from "@/util/common_function";
import { useSelector } from "react-redux";

const OffenseDetails = ({ report = {}, startingPoint = "" }) => {
  const [showOffenseModal, setShowOffenseModal] = useState(false);
  const [selectedOffense, setSelectedOffense]   = useState(null);
  const [form] = Form.useForm();

  const { bookingsFieldsEnabled } = useSelector((s) => s?.bookings);

  const sanctionOptions       = getOptionsFromRefList("Sanction_Type",     false, false, true) || [];
  const inmateViolationOptions = getOptionsFromRefList("Inmate_Violation", false, false, true) || [];

  // -----------------------------------------------------------------------
  // Table data
  // -----------------------------------------------------------------------
  const tableRows = (report.officerReportOffenses || []).map((offense, index) => {
    const violationOption = inmateViolationOptions.find(
      (opt) => opt.value === offense.inmateViolationId
    );
    return {
      id:        offense.sequence || index + 1,
      violation: violationOption?.label ?? offense.inmateViolationId ?? "N/A",
      comments:  offense.comments || "",
      offenseData: offense,
    };
  });

  // -----------------------------------------------------------------------
  // Sync selectedOffense → form
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!selectedOffense) return;
    const sanctions = selectedOffense.officerReportOffenseSanctions || [];
    const sanctionValues = {};
    sanctionOptions.forEach((option) => {
      sanctionValues[`sanction_${option.value}`] = sanctions.some(
        (s) => s.sanctionTypeId === option.value
      );
    });
    form.setFieldsValue({
      inmateViolation: selectedOffense.inmateViolationId || "",
      comments:        selectedOffense.comments          || "",
      sanctionEndDate: selectedOffense.sanctionEndDate
        ? dayjs(selectedOffense.sanctionEndDate, "MM/DD/YYYY")
        : null,
      ...sanctionValues,
    });
  }, [selectedOffense, form, sanctionOptions]);

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------
  const handleRowClick    = (row)  => setSelectedOffense(row.offenseData);
  const handleDetailsClick = ()   => { if (selectedOffense) setShowOffenseModal(true); };
  const handleCloseModal   = ()   => { setShowOffenseModal(false); setSelectedOffense(null); };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <>
      <StyledSubTableContainer>
        <TableHeader justify="space-between">
          <h2>List of offenses this inmate is being charged with</h2>
          <StyledFlex gap="10px">
            <Button onClick={handleDetailsClick} disabled={!selectedOffense}>
              Details
            </Button>
          </StyledFlex>
        </TableHeader>

        <Table
          headers={[
            { key: "id",        text: "#"         },
            { key: "violation", text: "Violation" },
            { key: "comments",  text: "Comments"  },
          ]}
          data={tableRows}
          variant="outlined"
          onRowClick={handleRowClick}
        />
      </StyledSubTableContainer>

      {/* Modal — field-level security via FieldSecurityProvider */}
      {showOffenseModal && (
        <CustomModal
          show={showOffenseModal}
          header="Offense Details"
          size="lg"
          hideFooter
          onHide={handleCloseModal}
        >
          <FieldSecurityProvider
            entity="OffenseDetails"
            startingPoint={startingPoint}
            bookingsFieldsEnabled={bookingsFieldsEnabled}
          >
            <Form form={form} layout="vertical">

              {/* ── Offense Information ───────────────────────────── */}
              <StyledSectionHeading>
                <span>Offense Information</span>
              </StyledSectionHeading>

              <StyledGridRow gutter={16} justify="start">
                <StyledGridCol xs={24}>
                  <SecureFormItem
                    fieldName="inmateViolation"
                    formItemProps={{ name: "inmateViolation", label: "Inmate Violation" }}
                  >
                    <Select
                      options={getOptionsFromRefList("Inmate_Violation", false, false, true)}
                      placeholder="Select Offense"
                    />
                  </SecureFormItem>
                </StyledGridCol>

                <StyledGridCol xs={24}>
                  <SecureFormItem
                    fieldName="comments"
                    formItemProps={{ name: "comments", label: "Comments" }}
                  >
                    <TextField />
                  </SecureFormItem>
                </StyledGridCol>
              </StyledGridRow>

              {/* ── Sanction Information ──────────────────────────── */}
              <StyledSectionHeading>
                <span>Sanction Information</span>
              </StyledSectionHeading>

              <StyledGridRow gutter={16} justify="start">
                <StyledGridCol xs={6}>
                  <SecureFormItem
                    fieldName="sanctionEndDate"
                    formItemProps={{ name: "sanctionEndDate", label: "Sanction End Date" }}
                  >
                    <CommonDatePicker showTime={false} />
                  </SecureFormItem>
                </StyledGridCol>
              </StyledGridRow>

              {/* Dynamic sanction checkboxes */}
              <StyledGridRow gutter={16}>
                {sanctionOptions.map((option) => (
                  <StyledGridCol xs={8} key={option.value}>
                    <SecureFormItem
                      fieldName={`sanction_${option.value}`}
                      formItemProps={{
                        name:          `sanction_${option.value}`,
                        label:         option.label,
                        layout:        "horizontal",
                        labelPostion:  "label-right",
                        valuePropName: "checked",
                      }}
                    >
                      <Checkbox />
                    </SecureFormItem>
                  </StyledGridCol>
                ))}
              </StyledGridRow>

            </Form>
          </FieldSecurityProvider>
        </CustomModal>
      )}
    </>
  );
};

export default OffenseDetails;